import { useEffect, useRef, useState } from 'react'

import PlusCircleIcon from '@heroicons/react/solid/PlusCircleIcon'
import XIcon from '@heroicons/react/solid/XIcon'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'

import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { EMAIL_PATTERN } from '../../lib/validator.utils'
import { ImportFormContext } from './useUserImportForm'
import { isAdminEmailNotConfirmed } from './utils'

const NAME_FIELD_MAX_LENGTH = 50

export interface EmailNameArrayInputProps {
  name: string
}

export const EmailNameArrayInput: React.FC<EmailNameArrayInputProps> = ({
  name,
}) => {
  const { $t } = useI18n()
  const {
    register,
    formState: { errors },
    entries,
    showManyAtOnce,
    appendEntry,
    removeEntry,
  } = useFormContext() as ImportFormContext

  const fieldsContainerRef = useRef<HTMLDivElement>(null)
  const { data: authToken } = useAuthToken()
  const [hoverRowIndex, setHoverRowIndex] = useState<number | undefined>(
    undefined,
  )

  useEffect(() => {
    const container = fieldsContainerRef.current

    // Focus on first the input element
    setTimeout(
      () =>
        (
          container?.firstElementChild?.firstElementChild
            ?.firstElementChild as HTMLElement
        )?.focus?.(),
      0,
    )
  }, [])

  const adminEmailNotConfirmed = isAdminEmailNotConfirmed(authToken.member)

  return (
    <div>
      <div className="max-h-96 overflow-y-auto p-2 -mx-2">
        <div ref={fieldsContainerRef}>
          {entries?.fields?.map((item, index) => {
            const fieldName = `${name}.${index}`
            return (
              <div
                className={clsx(
                  'group grid grid-cols-11 gap-2 p-2 -mx-2 rounded-md items-start ',
                  hoverRowIndex === index
                    ? 'hover:bg-surface-negative'
                    : 'hover:bg-surface-hovered',
                  adminEmailNotConfirmed && 'pointer-events-none',
                )}
                key={item.id}
              >
                <div className="col-span-5">
                  <FormControl.Input
                    placeholder={$t({
                      defaultMessage: 'Email address',
                      id: 'Generics.EmailAddress',
                    })}
                    data-testid={`${fieldName}.email`}
                    {...register(`${fieldName}.email`, {
                      required: $t({
                        defaultMessage: 'This field can’t be empty',
                        id: 'Generics.Validation.RequiredField',
                      }),
                      pattern: {
                        value: EMAIL_PATTERN,
                        message: $t({
                          defaultMessage: 'Invalid email address',
                          id: 'Generics.Validation.InvalidEmail',
                        }),
                      },
                    })}
                    // defaultValue={item.email}
                    error={errors?.[name]?.[index]?.email?.message}
                    invalid={!!errors?.[name]?.[index]?.email?.message}
                  />
                </div>

                <div className="col-span-5">
                  <FormControl.Input
                    data-testid={`${fieldName}.name`}
                    placeholder={$t({
                      defaultMessage: 'Name (optional)',
                      id: 'UserImport.EmailInputField.Name',
                    })}
                    maxLength={NAME_FIELD_MAX_LENGTH}
                    {...register(`${fieldName}.name`)}
                  />
                </div>

                <button
                  type="button"
                  className="cursor-pointer pt-2"
                  onClick={() => removeEntry(index)}
                  data-testid={`${fieldName}.remove`}
                  onMouseLeave={() => {
                    setHoverRowIndex(undefined)
                  }}
                  onMouseEnter={() => {
                    setHoverRowIndex(index)
                  }}
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      <div
        className={clsx(
          'text-sm flex items-center whitespace-pre',
          adminEmailNotConfirmed && 'pointer-events-none',
        )}
      >
        <T
          defaultMessage="<add-one>Add another</add-one> or <add-multi>multiple at once</add-multi>"
          id="UserImport.Actions.Add"
          values={{
            'add-one': chunks => (
              <Link
                as="button"
                type="button"
                onClick={appendEntry}
                className="inline-flex items-center"
              >
                <PlusCircleIcon className="h-6 me-1 inline" />
                {chunks}
              </Link>
            ),
            'add-multi': chunks => (
              <Link as="button" type="button" onClick={showManyAtOnce}>
                {chunks}
              </Link>
            ),
          }}
        />
      </div>
    </div>
  )
}
